import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckSquare, BarChart2, List, Type, Hash, Camera, Star, Calendar, Check, RotateCcw, X, ChevronRight, Clock, Package, RefreshCw, Search, AlertTriangle, MapPin, Truck } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';
import { useSociete } from '../../context/SocieteContext';
import { fmtDate as fmtDateUtil, fmtDateTime, todayInTz, nowInTz, addDays, wallTimeInTzToIso, isoToLocalInput } from '../../utils/dateUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// fmtDate local accepte un tz (fourni par le contexte société)
function fmtDate(s, tz = 'Europe/Paris') {
  return fmtDateUtil(s, tz);
}

const TYPE_ICONS = {
  checkbox: CheckSquare, niveau: BarChart2, select: List, text: Type,
  compteur: Hash, photo: Camera, note: Star, date: Calendar,
};

// ─── Saisie d'un critère ──────────────────────────────────────────────────────

function CritereInput({ critere, value, onChange, photoUrl, onPhotoUpload, uploadingPhoto }) {
  const Icon = TYPE_ICONS[critere.type] || Type;

  return (
    <div className="rounded-2xl border p-4 space-y-3"
      style={{ borderColor: value !== null && value !== undefined && value !== '' ? '#22C55E' : '#E2E8F0', background: 'white' }}>
      {/* Entête */}
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F0F9FF' }}>
          <Icon size={16} style={{ color: '#1e3a5f' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold" style={{ color: '#111827' }}>
            {critere.nom}
            {critere.requis && <span style={{ color: '#DC2626' }}> *</span>}
          </p>
          {critere.description && (
            <p className="text-sm" style={{ color: '#6B7280' }}>{critere.description}</p>
          )}
        </div>
        {/* Icône photo en haut à droite (sauf si le critère EST une photo) */}
        {critere.type !== 'photo' && onPhotoUpload && (
          photoUrl ? (
            <button onClick={() => onPhotoUpload(null)} className="flex-shrink-0 mt-0.5" title="Supprimer la photo">
              <img src={photoUrl} alt="" className="w-8 h-8 rounded-lg object-cover border-2" style={{ borderColor: '#22C55E' }} />
            </button>
          ) : (
            <label className="flex-shrink-0 mt-0.5 cursor-pointer" title="Ajouter une photo">
              {uploadingPhoto
                ? <RefreshCw size={16} className="animate-spin" style={{ color: '#9CA3AF' }} />
                : <Camera size={18} style={{ color: '#9CA3AF' }} />}
              <input type="file" accept="image/*" capture="environment" className="hidden"
                disabled={uploadingPhoto}
                onChange={e => e.target.files[0] && onPhotoUpload(e.target.files[0])} />
            </label>
          )
        )}
        {value !== null && value !== undefined && value !== '' && (
          <Check size={16} style={{ color: '#22C55E', flexShrink: 0, marginTop: 4 }} />
        )}
      </div>

      {/* Contrôle selon le type */}
      {critere.type === 'checkbox' && (
        <div className="flex gap-3">
          {[{ v: true, label: 'Oui', color: '#22C55E', bg: '#F0FDF4' }, { v: false, label: 'Non', color: '#EF4444', bg: '#FEF2F2' }].map(opt => (
            <button key={String(opt.v)} type="button"
              onClick={() => onChange(value === opt.v ? null : opt.v)}
              className="flex-1 py-4 rounded-2xl text-base font-bold border-2 transition-all"
              style={{
                borderColor: value === opt.v ? opt.color : '#E5E7EB',
                background: value === opt.v ? opt.bg : 'white',
                color: value === opt.v ? opt.color : '#9CA3AF',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {critere.type === 'niveau' && (
        <div className="flex flex-wrap gap-2">
          {(critere.niveaux || [0, 25, 50, 75, 100]).map(n => (
            <button key={n} type="button"
              onClick={() => onChange(value === n ? null : n)}
              className="flex-1 min-w-[56px] py-3 rounded-xl text-sm font-bold border-2 transition-all"
              style={{
                borderColor: value === n ? '#1e3a5f' : '#E5E7EB',
                background: value === n ? '#1e3a5f' : 'white',
                color: value === n ? 'white' : '#9CA3AF',
              }}>
              {n}%
            </button>
          ))}
        </div>
      )}

      {critere.type === 'select' && (
        <div className="space-y-2">
          {(critere.options || []).map(opt => (
            <button key={opt} type="button"
              onClick={() => onChange(value === opt ? null : opt)}
              className="w-full py-3 px-4 rounded-xl text-left text-sm font-semibold border-2 transition-all"
              style={{
                borderColor: value === opt ? '#1e3a5f' : '#E5E7EB',
                background: value === opt ? '#EFF6FF' : 'white',
                color: value === opt ? '#1e3a5f' : '#374151',
              }}>
              {opt}
            </button>
          ))}
        </div>
      )}

      {critere.type === 'text' && (
        <textarea
          value={value || ''}
          onChange={e => onChange(e.target.value || null)}
          rows={3}
          placeholder="Saisissez vos observations..."
          className="w-full px-4 py-3 rounded-xl border-2 text-sm resize-none outline-none"
          style={{ borderColor: '#E5E7EB', fontFamily: 'inherit' }}
        />
      )}

      {critere.type === 'compteur' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value ?? ''}
            onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
            placeholder="0"
            className="flex-1 min-w-0 px-3 py-3 rounded-xl border-2 text-lg font-bold text-center outline-none"
            style={{ borderColor: '#E5E7EB', color: '#1e3a5f' }}
          />
          {critere.unite && (
            <span className="text-base font-semibold flex-shrink-0" style={{ color: '#6B7280' }}>{critere.unite}</span>
          )}
        </div>
      )}

      {critere.type === 'note' && (
        <div className="flex gap-2">
          {Array.from({ length: critere.note_max || 5 }, (_, i) => i + 1).map(n => (
            <button key={n} type="button"
              onClick={() => onChange(value === n ? null : n)}
              className="flex-1 py-3 rounded-xl text-xl border-2 transition-all"
              style={{
                borderColor: n <= (value || 0) ? '#F59E0B' : '#E5E7EB',
                background: n <= (value || 0) ? '#FFFBEB' : 'white',
                color: n <= (value || 0) ? '#D97706' : '#D1D5DB',
              }}>
              ★
            </button>
          ))}
        </div>
      )}

      {critere.type === 'date' && (
        <input
          type="date"
          value={value || ''}
          onChange={e => onChange(e.target.value || null)}
          className="w-full px-4 py-4 rounded-xl border-2 text-lg outline-none"
          style={{ borderColor: '#E5E7EB', color: '#1e3a5f', fontFamily: 'inherit' }}
        />
      )}

      {critere.type === 'photo' && (
        <div className="flex flex-col items-center gap-2">
          {photoUrl ? (
            <div className="relative w-full rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <img src={photoUrl} alt="" className="w-full h-full object-cover" />
              <button onClick={() => onPhotoUpload && onPhotoUpload(null)}
                className="absolute top-1 right-1 rounded-full p-1" style={{ background: 'rgba(0,0,0,0.5)', color: 'white' }}>
                <X size={12} />
              </button>
            </div>
          ) : (
            <label className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl border-2 cursor-pointer font-semibold text-sm"
              style={{ borderColor: '#E5E7EB', color: '#6B7280', background: 'white' }}>
              {uploadingPhoto ? <RefreshCw size={16} className="animate-spin" /> : <Camera size={18} />}
              <span>{uploadingPhoto ? 'Upload...' : 'Prendre une photo'}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden"
                disabled={uploadingPhoto}
                onChange={e => e.target.files[0] && onPhotoUpload && onPhotoUpload(e.target.files[0])} />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Canvas de signature (réutilisé) ─────────────────────────────────────────

function SignatureCanvas({ onValidate }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [hasDrawing, setHasDrawing] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getPos(e, canvasRef.current); setHasDrawing(true); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const pos = getPos(e, canvasRef.current);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y); ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e3a5f'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke();
    lastPos.current = pos;
  };
  const stopDraw = () => { drawing.current = false; };
  const clear = () => { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); setHasDrawing(false); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: '#374151' }}>Signature</p>
        <button onClick={clear} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg"
          style={{ background: '#FEF3C7', color: '#92400E' }}>
          <RotateCcw size={12} /> Effacer
        </button>
      </div>
      <canvas ref={canvasRef} width={900} height={600}
        className="w-full rounded-2xl border-2"
        style={{ borderColor: '#CBD5E1', background: 'white', cursor: 'crosshair', touchAction: 'none' }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
      />
      <button onClick={() => {
        if (!hasDrawing) { toast.error('Veuillez signer'); return; }
        onValidate(canvasRef.current.toDataURL('image/png'));
      }}
        disabled={!hasDrawing}
        className="w-full py-4 rounded-2xl text-base font-bold text-white disabled:opacity-40"
        style={{ background: hasDrawing ? '#22C55E' : '#9CA3AF' }}>
        <Check size={18} className="inline mr-2" /> Valider la signature
      </button>
    </div>
  );
}

// ─── Vue saisie d'un BL ───────────────────────────────────────────────────────

function BLSaisie({ blId, onBack, onDone }) {
  const { activeSocieteTimezone: tz } = useSociete();
  const [bl, setBl] = useState(null);
  const [valeurs, setValeurs] = useState({});
  const [rempliPar, setRempliPar] = useState('');
  const [agents, setAgents] = useState([]);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [step, setStep] = useState('criteres'); // criteres | signature
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tolerance, setTolerance] = useState(0); // nb jours de tolérance configuré par admin
  const [dateEffective, setDateEffective] = useState(() => todayInTz(tz));
  const [signataire, setSignataire] = useState('');
  const [interlocuteurs, setInterlocuteurs] = useState([]);
  const [showSignatairePicker, setShowSignatairePicker] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState({});

  // Critère de type photo : upload réel (redimensionné) ; l'URL devient la valeur du critère
  const handleCriterePhoto = async (critereId, file) => {
    if (!file) { setValeurs(prev => ({ ...prev, [critereId]: null })); return; }
    setUploadingPhotos(prev => ({ ...prev, [critereId]: true }));
    try {
      const b64 = await resizePhoto(file);
      const res = await api.uploadPhotoTerrain({ image_b64: b64 });
      setValeurs(prev => ({ ...prev, [critereId]: res.data.url }));
    } catch { toast.error('Erreur upload photo'); }
    finally { setUploadingPhotos(prev => ({ ...prev, [critereId]: false })); }
  };

  // Changement matériel terrain
  const [showChangerMat, setShowChangerMat] = useState(false);
  const [matsDispos, setMatsDispos] = useState([]);
  const [matsLoading, setMatsLoading] = useState(false);
  const [matSearch, setMatSearch] = useState('');
  const [changingMat, setChangingMat] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.terrainGetBL(blId),
      api.getEmployes({ fonction_interne: 'Agent de contrôle', statut: 'actif' }),
      api.getParametres(),
    ]).then(([blRes, agentsRes, paramsRes]) => {
      setBl(blRes.data);
      const items = agentsRes.data || [];
      setAgents(Array.isArray(items) ? items : []);
      setTolerance(paramsRes.data?.terrain_bl_date_tolerance || 0);
      // Pour les BL retour, initialiser avec date+heure locale courante
      if (blRes.data?.type === 'retour') setDateEffective(nowInTz(tz));
      // Initialiser le signataire avec le 1er interlocuteur du contrat
      const interlos = blRes.data?.interlocuteurs_contrat || [];
      setInterlocuteurs(interlos);
      if (interlos.length > 0) setSignataire(interlos[0].nom || '');
    }).catch(() => {
      toast.error('BL introuvable');
    }).finally(() => setLoading(false));
  }, [blId]);

  const allRequiredFilled = () => {
    if (!bl) return false;
    return bl.criteres.filter(c => c.requis).every(c => {
      const v = valeurs[c.critere_id];
      return v !== null && v !== undefined && v !== '';
    });
  };

  const handleSubmit = async (signatureB64) => {
    if (!rempliPar.trim()) { toast.error('Nom requis'); return; }
    setSubmitting(true);
    try {
      await api.terrainSoumettresBL(blId, {
        rempli_par: rempliPar.trim(),
        valeurs,
        signature_image_b64: signatureB64,
        signataire_nom: signataire.trim() || undefined,
        ...((tolerance > 0 || bl?.type === 'retour') ? { date_effective: dateEffective } : {}),
      });
      toast.success('Bon de livraison signé !');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setSubmitting(false); }
  };

  const openChangerMat = async () => {
    setShowChangerMat(true);
    setMatsLoading(true);
    setMatSearch('');
    try {
      const r = await api.getMaterielsDisponibles({
        gamme_id: bl.gamme_id,
        date_debut: bl.date_debut,
        date_fin: bl.date_fin,
      });
      setMatsDispos((r.data || []).filter(m => m.disponible && m.materiel_id !== bl.materiel_id));
    } catch { toast.error('Impossible de charger les matériels'); }
    finally { setMatsLoading(false); }
  };

  const handleChangerMat = async (mat) => {
    setChangingMat(true);
    try {
      await api.terrainChangerMateriel(blId, mat.materiel_id);
      setBl(prev => ({
        ...prev,
        materiel_id: mat.materiel_id,
        materiel_nom: mat.nom || `${mat.marque || ''} ${mat.modele || ''}`.trim(),
        materiel_numero_serie: mat.numero_serie || '',
        materiel_numero_parc: mat.numero_parc || '',
      }));
      toast.success('Matériel modifié');
      setShowChangerMat(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setChangingMat(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center flex-1">
      <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#1e3a5f', borderTopColor: 'transparent' }} />
    </div>
  );

  if (!bl) return null;

  return (
    <div className="flex flex-col" style={{ minHeight: '100vh', background: '#F8FAFF' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        <button onClick={onBack} className="p-2 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={18} style={{ color: '#6B7280' }} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6B7280' }}>
            {bl.numero}
          </p>
          <h2 className="text-lg font-bold truncate" style={{ color: '#1e3a5f',  }}>
            {bl.materiel_nom || bl.gamme_nom || '—'}
          </h2>
        </div>
        <span className="text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0"
          style={{
            background: bl.type === 'retour' ? '#FFF7ED' : '#EFF6FF',
            color: bl.type === 'retour' ? '#C2410C' : '#1D4ED8',
          }}>
          {bl.type === 'retour' ? 'Retour' : bl.contrat_numero}
        </span>
      </div>
      <div className="mx-4 mt-4 rounded-2xl border p-4 space-y-1" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        <div className="flex justify-between text-sm">
          <span style={{ color: '#6B7280' }}>Client</span>
          <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.client_nom || '—'}</span>
        </div>
        {bl.categorie_nom && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>Catégorie</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.categorie_nom}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span style={{ color: '#6B7280' }}>Gamme</span>
          <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.gamme_nom || '—'}</span>
        </div>
        {/* Informations matériel */}
        {(bl.materiel_marque || bl.materiel_modele) && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>Marque / Modèle</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>
              {[bl.materiel_marque, bl.materiel_modele].filter(Boolean).join(' ')}
            </span>
          </div>
        )}
        {bl.materiel_numero_parc && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>N° de parc</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.materiel_numero_parc}</span>
          </div>
        )}
        {bl.materiel_immatriculation && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>Immatriculation</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.materiel_immatriculation}</span>
          </div>
        )}
        {bl.materiel_numero_serie && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>N° de série</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.materiel_numero_serie}</span>
          </div>
        )}
        {bl.date_debut && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>Période</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>
              {fmtDate(bl.date_debut)} → {fmtDate(bl.date_fin)}
            </span>
          </div>
        )}
        {bl.modele_controle_nom && (
          <div className="flex justify-between text-sm">
            <span style={{ color: '#6B7280' }}>Modèle contrôle</span>
            <span className="font-semibold" style={{ color: '#1e3a5f' }}>{bl.modele_controle_nom}</span>
          </div>
        )}
      </div>

      {/* Changer le matériel — uniquement BL livraison non signé */}
      {bl.type === 'livraison' && bl.statut !== 'signe' && (
        <div className="mx-4 mt-3">
          {!showChangerMat ? (
            <button
              data-testid="changer-materiel-btn"
              onClick={openChangerMat}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-sm font-semibold"
              style={{ borderColor: '#E2E8F0', background: 'white', color: '#6B7280' }}>
              <RefreshCw size={14} /> Changer le matériel (défaut)
            </button>
          ) : (
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: '#E2E8F0', background: 'white' }}>
              <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: '#E2E8F0' }}>
                <AlertTriangle size={14} style={{ color: '#D97706' }} />
                <span className="text-sm font-bold" style={{ color: '#92400E' }}>Changer le matériel</span>
                <button onClick={() => setShowChangerMat(false)} className="ml-auto p-1 rounded">
                  <X size={14} style={{ color: '#6B7280' }} />
                </button>
              </div>
              <div className="px-3 py-2 border-b" style={{ borderColor: '#F3F4F6' }}>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
                  <input value={matSearch} onChange={e => setMatSearch(e.target.value)}
                    placeholder="N° parc, série, nom..."
                    className="w-full pl-8 pr-3 py-2 rounded-xl border text-sm outline-none"
                    style={{ borderColor: '#E2E8F0' }} />
                </div>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {matsLoading ? (
                  <p className="px-4 py-4 text-sm text-center" style={{ color: '#9CA3AF' }}>Chargement...</p>
                ) : matsDispos.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-center" style={{ color: '#9CA3AF' }}>Aucun matériel disponible</p>
                ) : matsDispos
                    .filter(m => !matSearch || `${m.nom || ''} ${m.numero_parc || ''} ${m.numero_serie || ''}`.toLowerCase().includes(matSearch.toLowerCase()))
                    .map((m, i) => (
                      <button key={m.materiel_id} type="button"
                        onClick={() => handleChangerMat(m)}
                        disabled={changingMat}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors disabled:opacity-40"
                        style={{ borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
                        <Package size={14} style={{ color: '#1e3a5f', flexShrink: 0 }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: '#111827' }}>
                            {m.nom || `${m.marque || ''} ${m.modele || ''}`.trim() || '—'}
                          </p>
                          <p className="text-xs" style={{ color: '#6B7280' }}>
                            {[m.numero_parc && `Parc: ${m.numero_parc}`, m.numero_serie && `Série: ${m.numero_serie}`].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </button>
                    ))
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contenu principal */}
      {step === 'criteres' ? (
        <div className="flex-1 px-4 py-4 space-y-3 overflow-y-auto" style={{ paddingBottom: 120 }}>
          {/* Sélection du signataire client */}
          <div className="rounded-2xl border p-4" style={{ borderColor: '#E2E8F0', background: 'white' }}>
            <p className="text-sm font-bold mb-2" style={{ color: '#374151',  }}>
              Signataire du bon *
            </p>
            {bl.statut === 'signe' ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl"
                style={{ background: '#F0FDF4', border: '2px solid #22C55E' }}>
                <span className="font-semibold text-base flex-1" style={{ color: '#111827' }}>{signataire || '—'}</span>
                <Check size={18} style={{ color: '#22C55E' }} />
              </div>
            ) : interlocuteurs.length > 0 ? (
              <button
                type="button"
                data-testid="signataire-picker-btn"
                onClick={() => setShowSignatairePicker(true)}
                className="w-full flex items-center justify-between px-4 py-4 rounded-xl border-2 text-base text-left"
                style={{
                  borderColor: signataire ? '#22C55E' : '#E5E7EB',
                  background: signataire ? '#F0FDF4' : '#F9FAFB',
                  color: signataire ? '#111827' : '#9CA3AF',
                }}>
                <span className="font-semibold">{signataire || 'Sélectionner le signataire...'}</span>
                {signataire
                  ? <Check size={18} style={{ color: '#22C55E', flexShrink: 0 }} />
                  : <ChevronRight size={18} style={{ color: '#9CA3AF', flexShrink: 0 }} />
                }
              </button>
            ) : (
              <input
                value={signataire}
                onChange={e => setSignataire(e.target.value)}
                placeholder="Nom du signataire"
                className="w-full px-4 py-3 rounded-xl border-2 text-base outline-none"
                style={{ borderColor: signataire.trim() ? '#22C55E' : '#E5E7EB', fontFamily: 'inherit' }}
                data-testid="signataire-input"
              />
            )}
            {interlocuteurs.length === 0 && !bl.statut === 'signe' && (
              <p className="text-xs mt-1.5" style={{ color: '#9CA3AF' }}>
                Aucun interlocuteur dans le contrat — saisissez le nom manuellement
              </p>
            )}
          </div>

          {/* Sélection de l'agent de contrôle */}
          <div className="rounded-2xl border p-4" style={{ borderColor: '#E2E8F0', background: 'white' }}>
            <p className="text-sm font-bold mb-2" style={{ color: '#374151',  }}>
              Agent de contrôle *
            </p>
            {agents.length > 0 ? (
              <button
                type="button"
                onClick={() => setShowAgentPicker(true)}
                data-testid="agent-picker-btn"
                className="w-full flex items-center justify-between px-4 py-4 rounded-xl border-2 text-base text-left"
                style={{
                  borderColor: rempliPar ? '#22C55E' : '#E5E7EB',
                  background: rempliPar ? '#F0FDF4' : '#F9FAFB',
                  color: rempliPar ? '#111827' : '#9CA3AF',
                  fontFamily: 'inherit',
                }}>
                <span className="font-semibold">{rempliPar || 'Sélectionner un agent...'}</span>
                {rempliPar
                  ? <Check size={18} style={{ color: '#22C55E', flexShrink: 0 }} />
                  : <ChevronRight size={18} style={{ color: '#9CA3AF', flexShrink: 0 }} />
                }
              </button>
            ) : (
              <input
                value={rempliPar}
                onChange={e => setRempliPar(e.target.value)}
                placeholder="Votre nom complet"
                className="w-full px-4 py-3 rounded-xl border-2 text-base outline-none"
                style={{ borderColor: rempliPar.trim() ? '#22C55E' : '#E5E7EB', fontFamily: 'inherit' }}
                data-testid="rempli-par-input"
              />
            )}
            {agents.length === 0 && (
              <p className="text-xs mt-1.5" style={{ color: '#9CA3AF' }}>
                Aucun employé avec la fonction "Agent de contrôle" — attribuez-la dans RH → Employés
              </p>
            )}
          </div>

          {/* Date effective :
              - Retour  → toujours visible (date réelle de restitution)
              - Livraison → visible seulement si tolérance > 0 */}
          {(bl?.type === 'retour' || tolerance > 0) && (
            <div className="rounded-2xl border p-4 space-y-2"
              style={{ borderColor: bl?.type === 'retour' ? '#FDBA74' : '#E2E8F0', background: 'white' }}>
              <div className="flex items-center gap-2">
                <Calendar size={15} style={{ color: bl?.type === 'retour' ? '#C2410C' : '#6B7280', flexShrink: 0 }} />
                <p className="text-sm font-bold" style={{ color: '#374151',  }}>
                  Date réelle {bl?.type === 'retour' ? 'du retour' : 'de la livraison'}
                  {tolerance > 0 && (
                    <span className="text-xs font-normal ml-1" style={{ color: '#6B7280' }}>
                      (±{tolerance} j.)
                    </span>
                  )}
                </p>
              </div>
              <input
                type={bl?.type === 'retour' ? 'datetime-local' : 'date'}
                value={dateEffective}
                onChange={e => setDateEffective(e.target.value)}
                min={bl?.type === 'retour'
                  ? (bl?.date_livraison ? bl.date_livraison + 'T00:00' : undefined)
                  : (bl?.date_debut_contrat || undefined)}
                max={bl?.type === 'retour'
                  ? nowInTz(tz)
                  : (tolerance > 0 ? addDays(todayInTz(tz), tolerance) : undefined)}
                data-testid="date-effective-input"
                className="w-full px-4 py-3 rounded-xl border-2 text-base outline-none"
                style={{ borderColor: dateEffective ? '#22C55E' : '#E5E7EB', fontFamily: 'inherit' }}
              />
              {bl?.type === 'retour' && bl?.date_livraison && (
                <p className="text-xs" style={{ color: '#9CA3AF' }}>
                  Date de retour — au plus tôt le {bl.date_livraison.split('-').reverse().join('/')} (date de livraison).
                </p>
              )}
              {bl?.type === 'retour' && !bl?.date_livraison && (
                <p className="text-xs" style={{ color: '#9CA3AF' }}>
                  Date à laquelle le matériel a été effectivement restitué.
                </p>
              )}
            </div>
          )}

          {/* Modale de sélection du signataire */}
          {showSignatairePicker && (
            <div className="fixed inset-0 z-50 flex flex-col"
              style={{ background: 'rgba(0,0,0,0.5)' }}>
              <div className="flex-1" onClick={() => setShowSignatairePicker(false)} />
              <div className="rounded-t-3xl overflow-hidden"
                style={{ background: 'white', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b"
                  style={{ borderColor: '#E2E8F0' }}>
                  <p className="text-base font-bold" style={{ color: '#111827',  }}>
                    Choisir le signataire
                  </p>
                  <button onClick={() => setShowSignatairePicker(false)}
                    className="p-2 rounded-xl" style={{ background: '#F3F4F6' }}>
                    <X size={18} style={{ color: '#6B7280' }} />
                  </button>
                </div>
                <div className="overflow-y-auto p-4 space-y-2">
                  {interlocuteurs.map((c, i) => {
                    const isSelected = signataire === c.nom;
                    return (
                      <button key={c.id || i}
                        data-testid={`signataire-option-${c.id || i}`}
                        onClick={() => { setSignataire(c.nom); setShowSignatairePicker(false); }}
                        className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-all"
                        style={{
                          borderColor: isSelected ? '#22C55E' : '#E2E8F0',
                          background: isSelected ? '#F0FDF4' : 'white',
                        }}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                          style={{ background: '#EFF6FF', color: '#1e3a5f' }}>
                          {(c.nom || '').split(' ').map(w => w[0] || '').slice(0, 2).join('').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold truncate" style={{ color: '#111827',  }}>
                            {c.nom}
                          </p>
                          {c.email && (
                            <p className="text-xs truncate" style={{ color: '#6B7280' }}>{c.email}</p>
                          )}
                        </div>
                        {isSelected && <Check size={20} style={{ color: '#22C55E', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Modale de sélection de l'agent */}
          {showAgentPicker && (
            <div className="fixed inset-0 z-50 flex flex-col"
              style={{ background: 'rgba(0,0,0,0.5)' }}>
              <div className="flex-1" onClick={() => setShowAgentPicker(false)} />
              <div className="rounded-t-3xl overflow-hidden"
                style={{ background: 'white', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b"
                  style={{ borderColor: '#E2E8F0' }}>
                  <p className="text-base font-bold" style={{ color: '#111827',  }}>
                    Choisir l'agent de contrôle
                  </p>
                  <button onClick={() => setShowAgentPicker(false)}
                    className="p-2 rounded-xl" style={{ background: '#F3F4F6' }}>
                    <X size={18} style={{ color: '#6B7280' }} />
                  </button>
                </div>
                <div className="overflow-y-auto p-4 space-y-2">
                  {agents.map(a => {
                    const fullName = [a.personne?.prenom || a.prenom, a.personne?.nom || a.nom].filter(Boolean).join(' ');
                    const isSelected = rempliPar === fullName;
                    const key = a.employe_id || a.personne_id;
                    return (
                      <button key={key}
                        data-testid={`agent-option-${key}`}
                        onClick={() => { setRempliPar(fullName); setShowAgentPicker(false); }}
                        className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-all"
                        style={{
                          borderColor: isSelected ? '#22C55E' : '#E2E8F0',
                          background: isSelected ? '#F0FDF4' : 'white',
                        }}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                          style={{ background: '#EFF6FF', color: '#1e3a5f' }}>
                          {((a.personne?.prenom || a.prenom || '')[0] || '').toUpperCase()}{((a.personne?.nom || a.nom || '')[0] || '').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold truncate" style={{ color: '#111827',  }}>
                            {fullName}
                          </p>
                        </div>
                        {isSelected && <Check size={20} style={{ color: '#22C55E', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {bl.criteres.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2"
              style={{ color: '#9CA3AF' }}>
              <Package size={36} style={{ opacity: 0.3 }} />
              <p className="text-base font-medium">Aucun critère à remplir</p>
              <p className="text-sm">Ce bon est une livraison simple sans contrôle</p>
            </div>
          ) : bl.criteres.map(c => (
            <CritereInput
              key={c.critere_id}
              critere={c}
              value={valeurs[c.critere_id] ?? null}
              onChange={v => setValeurs(prev => ({ ...prev, [c.critere_id]: v }))}
              photoUrl={c.type === 'photo' ? (valeurs[c.critere_id] || null) : undefined}
              onPhotoUpload={c.type === 'photo' ? (f => handleCriterePhoto(c.critere_id, f)) : undefined}
              uploadingPhoto={c.type === 'photo' ? !!uploadingPhotos[c.critere_id] : false}
            />
          ))}
        </div>
      ) : (
        <div className="flex-1 px-4 py-4" style={{ paddingBottom: 24 }}>
          <SignatureCanvas
            onValidate={(b64) => handleSubmit(b64)}
          />
        </div>
      )}

      {/* Footer sticky */}
      {step === 'criteres' && (
        <div className="sticky bottom-0 px-4 py-4 border-t"
          style={{ borderColor: '#E2E8F0', background: 'white' }}>
          {!allRequiredFilled() && bl.criteres.some(c => c.requis) && (
            <p className="text-xs text-center mb-2" style={{ color: '#EF4444' }}>
              Remplissez tous les critères obligatoires *
            </p>
          )}
          <button
            onClick={() => {
              if (!signataire.trim()) { toast.error('Sélectionnez un signataire'); return; }
              if (!rempliPar.trim()) { toast.error('Saisissez votre nom'); return; }
              if (!allRequiredFilled()) { toast.error('Des critères obligatoires sont manquants'); return; }
              setStep('signature');
            }}
            data-testid="btn-proceed-signature"
            className="w-full py-4 rounded-2xl text-base font-bold text-white"
            style={{ background: '#1e3a5f' }}>
            Procéder à la signature
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Vue liste BL par contrat ─────────────────────────────────────────────────

function BLListeContrat({ groupe, onSelectBL, onBack }) {
  return (
    <div className="flex flex-col" style={{ minHeight: '100vh', background: '#F8FAFF' }}>
      <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        <button onClick={onBack} className="p-2 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={18} style={{ color: '#6B7280' }} />
        </button>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6B7280' }}>Contrat</p>
          <h2 className="text-lg font-bold" style={{ color: '#1e3a5f',  }}>
            {groupe.contrat_numero} — {groupe.client_nom}
          </h2>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#9CA3AF' }}>
          {groupe.bls.length} bon{groupe.bls.length > 1 ? 's' : ''} de {groupe.bls.some(b => b.type === 'retour') ? 'retour' : 'livraison'} en attente
        </p>
        {groupe.bls.map(bl => (
          <button key={bl.bl_id}
            data-testid={`bl-item-${bl.bl_id}`}
            onClick={() => onSelectBL(bl.bl_id, bl.source || 'v1')}
            className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-transform"
            style={{ borderColor: '#E2E8F0', background: 'white' }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#EFF6FF' }}>
              <Package size={22} style={{ color: '#1e3a5f' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold truncate" style={{ color: '#111827',  }}>
                {bl.materiel_nom || bl.gamme_nom || '—'}
              </p>
              {bl.categorie_nom && (
                <p className="text-sm font-semibold" style={{ color: '#6B7280' }}>{bl.categorie_nom}</p>
              )}
              <p className="text-sm" style={{ color: '#6B7280' }}>{bl.numero}</p>
              {bl.modele_controle_nom && (
                <p className="text-xs" style={{ color: '#9CA3AF' }}>{bl.modele_controle_nom}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: '#FFFBEB', color: '#D97706' }}>En attente</span>
              <ChevronRight size={16} style={{ color: '#9CA3AF' }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Saisie BL V2 (workflow terrain pour contrats V2) ─────────────────────────

// Redimensionne une image à max 1280×720 et compresse à ≤300Ko (côté client)
async function resizePhoto(file) {
  return new Promise(resolve => {
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = e => {
      img.onload = () => {
        const MAX_W = 1280, MAX_H = 720;
        let { width: w, height: h } = img;
        if (w > MAX_W || h > MAX_H) {
          const ratio = Math.min(MAX_W / w, MAX_H / h);
          w = Math.round(w * ratio); h = Math.round(h * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        let quality = 0.85;
        const tryEncode = () => {
          const b64 = canvas.toDataURL('image/jpeg', quality);
          const bytes = Math.round((b64.length * 3) / 4);
          if (bytes > 300_000 && quality > 0.4) { quality -= 0.1; tryEncode(); }
          else resolve(b64);
        };
        tryEncode();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ─── Lecture seule BL V2 signé ───────────────────────────────────────────────

function BLLectureV2({ blId, onBack }) {
  const [bl, setBl]             = useState(null);
  const [controles, setControles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [lightbox, setLightbox] = useState(null); // URL photo en plein écran
  const { activeSocieteTimezone: tz } = useSociete();

  useEffect(() => {
    setLoading(true);
    Promise.all([api.terrainGetBLV2(blId), api.getControlesV2(blId)])
      .then(([blRes, ctlRes]) => { setBl(blRes.data); setControles(ctlRes.data || []); })
      .catch(() => toast.error('Impossible de charger le BL'))
      .finally(() => setLoading(false));
  }, [blId]);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><RefreshCw className="animate-spin" size={28} style={{ color: '#1e3a5f' }} /></div>;
  if (!bl)    return null;

  const isRetour = bl.type_mouvement === 'RETOUR';
  const firstLigne = (bl.lignes || [])[0];

  // Construire un index { ligne_bl_id -> controle } pour retrouver les valeurs
  const ctlByLigne = {};
  controles.forEach(c => { ctlByLigne[c.ligne_bl_id] = c; });

  return (
    <div className="flex flex-col" style={{ minHeight: '100vh', background: '#F8FAFF' }}>

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ background: 'white', borderColor: '#E2E8F0' }}>
        <button onClick={onBack} className="p-2 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={18} style={{ color: '#6B7280' }} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6B7280' }}>{bl.numero}</p>
          <h2 className="text-lg font-bold truncate" style={{ color: '#1e3a5f' }}>
            {firstLigne?.equipement_nom || firstLigne?.gamme_nom || bl.client_nom || '—'}
          </h2>
        </div>
        <span className="text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0"
          style={{ background: '#F0FDF4', color: '#15803D' }}>Signé</span>
      </div>

      <div className="px-4 py-4 space-y-4 overflow-y-auto" style={{ paddingBottom: 32 }}>

        {/* Bloc info */}
        {(bl.lignes || []).map((l, i) => (
          <div key={i} className="rounded-2xl border p-4 space-y-1" style={{ borderColor: '#E2E8F0', background: 'white' }}>
            {bl.client_nom && <InfoRow label="Client" value={bl.client_nom} />}
            {l.categorie_nom && <InfoRow label="Catégorie" value={l.categorie_nom} />}
            {l.gamme_nom && <InfoRow label="Gamme" value={l.gamme_nom} />}
            {l.equipement_nom && <InfoRow label="Marque / Modèle" value={l.equipement_nom} />}
            {l.numero_parc && <InfoRow label="N° de parc" value={l.numero_parc} />}
            {l.immatriculation && <InfoRow label="Immatriculation" value={l.immatriculation} />}
            {l.numero_serie && <InfoRow label="N° de série" value={l.numero_serie} />}
            {(l.date_debut_prevue || l.date_fin_prevue) && (
              <InfoRow label="Période" value={`${fmtDate(l.date_debut_prevue)} → ${fmtDate(l.date_fin_prevue)}`} />
            )}
          </div>
        ))}

        {/* Signataire / agent / date */}
        {(bl.signataire_nom || bl.rempli_par || bl.date_effective) && (
          <div className="rounded-2xl border p-4 space-y-1" style={{ borderColor: '#E2E8F0', background: 'white' }}>
            {bl.signataire_nom && <InfoRow label="Signataire" value={bl.signataire_nom} />}
            {bl.rempli_par && <InfoRow label="Agent de contrôle" value={bl.rempli_par} />}
            {bl.date_effective && (
              <InfoRow label={isRetour ? 'Date réelle retour' : 'Date réelle livraison'}
                value={fmtDateTime(bl.date_effective, tz)} />
            )}
          </div>
        )}

        {/* Contrôles par ligne */}
        {(bl.lignes || []).map((l, idx) => {
          const ligneId = l.ligne_bl_id || l.ligne_id;
          const ctl = ctlByLigne[ligneId];
          if (!ctl && !l.criteres?.length) return null;
          return (
            <div key={idx}>
              {(bl.lignes || []).length > 1 && (
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#9CA3AF' }}>
                  {l.categorie_nom || l.gamme_nom || `Machine ${idx + 1}`}
                </p>
              )}
              {/* Critères + valeurs */}
              {(l.criteres || []).length > 0 && ctl && (
                <div className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: '#E2E8F0' }}>
                  {(l.criteres || []).map((c, ci) => {
                    const ligneCtl = (ctl.lignes || []).find(lc => lc.critere_id === c.critere_id);
                    const val = ligneCtl?.valeur_mesuree;
                    const photoUrl = ligneCtl?.photo_url;
                    const Icon = TYPE_ICONS[c.type] || Type;
                    return (
                      <div key={ci} className="flex items-start gap-3 px-4 py-3 border-b last:border-0"
                        style={{ borderColor: '#F1F5F9' }}>
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: '#F0F9FF' }}>
                          <Icon size={13} style={{ color: '#1e3a5f' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold" style={{ color: '#374151' }}>{c.nom}</p>
                          <p className="text-sm mt-0.5" style={{ color: val ? '#111827' : '#D1D5DB' }}>
                            {val || '—'}{c.unite ? ` ${c.unite}` : ''}
                          </p>
                        </div>
                        {photoUrl && (
                          <button onClick={() => setLightbox(photoUrl)} className="flex-shrink-0">
                            <img src={photoUrl} alt="" className="w-10 h-10 rounded-lg object-cover border"
                              style={{ borderColor: '#E2E8F0' }} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Photos générales du contrôle */}
              {ctl && (ctl.photos_urls || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#9CA3AF' }}>
                    Photos générales
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {ctl.photos_urls.map((url, pi) => (
                      <button key={pi} onClick={() => setLightbox(url)}
                        className="rounded-xl overflow-hidden" style={{ aspectRatio: '1' }}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* PDF BL signé */}
        {bl.pdf_bl_signe_url && (
          <a href={bl.pdf_bl_signe_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border text-sm font-semibold"
            style={{ borderColor: '#E2E8F0', color: '#1e3a5f', background: 'white' }}>
            <Package size={15} /> Voir le PDF signé
          </a>
        )}
      </div>

      {/* Lightbox photo */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain" style={{ maxHeight: '90vh' }} />
          <button className="absolute top-4 right-4 p-2 rounded-full" style={{ background: 'rgba(255,255,255,0.15)', color: 'white' }}>
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

// Petite aide pour la lisibilité
function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span style={{ color: '#6B7280' }}>{label}</span>
      <span className="font-semibold text-right ml-4" style={{ color: '#1e3a5f' }}>{value}</span>
    </div>
  );
}

// ─── Saisie BL V2 (tablette) ──────────────────────────────────────────────────

function BLSaisieV2({ blId, onBack, onDone }) {
  const [bl, setBl]               = useState(null);
  const [loading, setLoading]     = useState(true);
  const [step, setStep]           = useState('criteres'); // criteres | photos | signature
  const [signataire, setSignataire]         = useState('');
  const [showSignatairePicker, setShowSignatairePicker] = useState(false);
  const [signataireManual, setSignataireManual] = useState(false);
  const [rempliPar, setRempliPar]           = useState('');
  const [agents, setAgents]                 = useState([]);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [critereValues, setCritereValues]   = useState({});
  const [criterePhotos, setCriterePhotos]   = useState({}); // { [ligneId]: { [critereId]: url } }
  const [uploadingCriterePhoto, setUploadingCriterePhoto] = useState({}); // { [ligneId_critereId]: bool }
  const [generalPhotos, setGeneralPhotos]   = useState([]);
  const { activeSocieteTimezone: tz } = useSociete();
  const [dateEffective, setDateEffective]   = useState('');
  const [dateModified, setDateModified]     = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.terrainGetBLV2(blId),
      api.getEmployes({ fonction_interne: 'Agent de contrôle', statut: 'actif' }),
    ])
      .then(([blRes, agentsRes]) => {
        setBl(blRes.data);
        setAgents(agentsRes.data || []);
        const interlos = blRes.data?.interlocuteurs_contrat || [];
        if (interlos.length > 0) setSignataire(interlos[0].nom || '');
      })
      .catch(() => toast.error('BL V2 introuvable'))
      .finally(() => setLoading(false));
  }, [blId]);

  // Initialiser date_effective depuis date_prevue une fois le tz chargé (évite le race condition)
  useEffect(() => {
    if (!dateModified && bl?.date_prevue && tz) {
      setDateEffective(isoToLocalInput(bl.date_prevue, tz));
    }
  }, [bl?.date_prevue, tz, dateModified]);

  const setCritere = (ligneBlId, critereId, val) =>
    setCritereValues(prev => ({ ...prev, [ligneBlId]: { ...(prev[ligneBlId] || {}), [critereId]: val } }));

  const handleCriterePhoto = async (ligneBlId, critereId, file) => {
    if (!file) {
      setCriterePhotos(prev => ({ ...prev, [ligneBlId]: { ...(prev[ligneBlId] || {}), [critereId]: null } }));
      return;
    }
    const key = `${ligneBlId}_${critereId}`;
    setUploadingCriterePhoto(prev => ({ ...prev, [key]: true }));
    try {
      const b64 = await resizePhoto(file);
      const res = await api.uploadPhotoTerrain({ image_b64: b64 });
      setCriterePhotos(prev => ({ ...prev, [ligneBlId]: { ...(prev[ligneBlId] || {}), [critereId]: res.data.url } }));
    } catch { toast.error('Erreur upload photo'); }
    finally { setUploadingCriterePhoto(prev => ({ ...prev, [key]: false })); }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const b64 = await resizePhoto(file);
      const res = await api.uploadPhotoTerrain({ image_b64: b64 });
      setGeneralPhotos(prev => [...prev, res.data.url]);
    } catch { toast.error('Erreur upload photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSubmit = async (signatureB64) => {
    if (!signataire.trim()) { toast.error('Nom du signataire requis'); return; }
    setSubmitting(true);
    try {
      const controles = (bl.lignes || []).map(l => ({
        ligne_bl_id: l.ligne_bl_id || l.ligne_id,
        materiel_id: l.equipement_id || '',
        modele_controle_id: l.modele_controle_id || null,
        type_controle: bl.type_mouvement === 'RETOUR' ? 'RETOUR' : 'SORTIE',
        photos_urls: generalPhotos,
        lignes: (l.criteres || []).map(c => {
          const ligneId = l.ligne_bl_id || l.ligne_id;
          return {
            critere_id: c.critere_id,
            nom_critere: c.nom,
            type_critere: c.type,
            unite: c.unite || null,
            valeur_mesuree: String(critereValues[ligneId]?.[c.critere_id] ?? ''),
            photo_url: criterePhotos[ligneId]?.[c.critere_id] || null,
          };
        }),
      }));
      if (controles.length > 0) await api.soumettreControlesV2(blId, controles);
      await api.terrainSoumettreBLV2(blId, {
        signataire_nom: signataire.trim(),
        rempli_par: rempliPar.trim() || null,
        signature_image_b64: signatureB64,
        date_effective: wallTimeInTzToIso(dateEffective, tz),
      });
      toast.success(bl?.type_mouvement === 'RETOUR' ? 'Retour confirmé !' : 'Livraison signée !');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><RefreshCw className="animate-spin" size={32} style={{ color: '#1e3a5f' }} /></div>;
  if (!bl)    return <div className="p-8 text-center"><AlertTriangle size={32} style={{ color: '#EF4444' }} /><p>BL introuvable</p></div>;

  const isRetour    = bl.type_mouvement === 'RETOUR';
  const interlos    = bl.interlocuteurs_contrat || [];

  return (
    <div className="flex flex-col" style={{ minHeight: '100vh', background: '#F8FAFF' }}>

      {/* ── Header (même style V1) ── */}
      <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ background: 'white', borderColor: '#E2E8F0' }}>
        <button onClick={onBack} className="p-2 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={18} style={{ color: '#6B7280' }} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6B7280' }}>
            {bl.numero}
          </p>
          <h2 className="text-lg font-bold truncate" style={{ color: '#1e3a5f' }}>
            {(bl.lignes && bl.lignes[0])
              ? (bl.lignes[0].equipement_nom || bl.lignes[0].gamme_nom || bl.client_nom || '—')
              : (bl.client_nom || '—')}
          </h2>
        </div>
        <span className="text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0"
          style={{ background: isRetour ? '#FFF7ED' : '#EFF6FF', color: isRetour ? '#C2410C' : '#1D4ED8' }}>
          {bl.contrat_numero || (isRetour ? 'Retour' : 'Livraison')}
        </span>
      </div>

      {/* ── Étape Critères ── */}
      {step === 'criteres' && (
        <div className="flex-1 px-4 py-4 space-y-4 overflow-y-auto" style={{ paddingBottom: 100 }}>

          {/* Bloc info matériel (V1 : 2 colonnes, en premier) */}
          {(bl.lignes || []).map((l, i) => (
            <div key={i} className="rounded-2xl border p-4 space-y-1" style={{ borderColor: '#E2E8F0', background: 'white' }}>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#6B7280' }}>Client</span>
                <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{bl.client_nom || '—'}</span>
              </div>
              {l.categorie_nom && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>Catégorie</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.categorie_nom}</span>
                </div>
              )}
              {l.gamme_nom && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>Gamme</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.gamme_nom}</span>
                </div>
              )}
              {l.equipement_nom && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>Marque / Modèle</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.equipement_nom}</span>
                </div>
              )}
              {l.numero_parc && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>N° de parc</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.numero_parc}</span>
                </div>
              )}
              {l.immatriculation && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>Immatriculation</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.immatriculation}</span>
                </div>
              )}
              {l.numero_serie && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>N° de série</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.numero_serie}</span>
                </div>
              )}
              {(l.date_debut_prevue || l.date_fin_prevue) && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>Période</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>
                    {fmtDate(l.date_debut_prevue)} → {fmtDate(l.date_fin_prevue)}
                  </span>
                </div>
              )}
              {l.modele_controle_nom && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#6B7280' }}>Modèle contrôle</span>
                  <span className="font-semibold text-right" style={{ color: '#1e3a5f' }}>{l.modele_controle_nom}</span>
                </div>
              )}
            </div>
          ))}

          {/* Signataire */}
          <div className="rounded-2xl border p-4" style={{ borderColor: '#E2E8F0', background: 'white' }}>
            <p className="text-sm font-bold mb-2" style={{ color: '#374151' }}>Signataire du bon *</p>
            {interlos.length > 0 && !signataireManual ? (
              <button type="button" data-testid="blv2-signataire-picker-btn"
                onClick={() => setShowSignatairePicker(true)}
                className="w-full flex items-center justify-between px-4 py-4 rounded-xl border-2 text-base text-left"
                style={{ borderColor: signataire ? '#22C55E' : '#E5E7EB', background: signataire ? '#F0FDF4' : '#F9FAFB', color: signataire ? '#111827' : '#9CA3AF' }}>
                <span className="font-semibold">{signataire || 'Sélectionner le signataire...'}</span>
                {signataire ? <Check size={18} style={{ color: '#22C55E', flexShrink: 0 }} /> : <ChevronRight size={18} style={{ color: '#9CA3AF', flexShrink: 0 }} />}
              </button>
            ) : (
              <input value={signataire} onChange={e => setSignataire(e.target.value)}
                placeholder="Nom du signataire" data-testid="blv2-signataire-input"
                className="w-full px-4 py-3 rounded-xl border-2 text-base outline-none"
                style={{ borderColor: signataire.trim() ? '#22C55E' : '#E5E7EB', fontFamily: 'inherit' }} />
            )}
            {interlos.length > 0 && (
              <button type="button" data-testid="blv2-signataire-toggle-manual"
                onClick={() => setSignataireManual(m => !m)}
                className="mt-2 text-xs font-semibold"
                style={{ color: '#1D4ED8' }}>
                {signataireManual ? '← Choisir parmi les interlocuteurs du contrat' : 'Saisir un autre nom manuellement'}
              </button>
            )}
          </div>

          {/* Agent de contrôle */}
          <div className="rounded-2xl border p-4" style={{ borderColor: '#E2E8F0', background: 'white' }}>
            <p className="text-sm font-bold mb-2" style={{ color: '#374151' }}>Agent de contrôle *</p>
            {agents.length > 0 ? (
              <button type="button" data-testid="blv2-agent-picker-btn"
                onClick={() => setShowAgentPicker(true)}
                className="w-full flex items-center justify-between px-4 py-4 rounded-xl border-2 text-base text-left"
                style={{ borderColor: rempliPar ? '#22C55E' : '#E5E7EB', background: rempliPar ? '#F0FDF4' : '#F9FAFB', color: rempliPar ? '#111827' : '#9CA3AF', fontFamily: 'inherit' }}>
                <span className="font-semibold">{rempliPar || 'Sélectionner un agent...'}</span>
                {rempliPar ? <Check size={18} style={{ color: '#22C55E', flexShrink: 0 }} /> : <ChevronRight size={18} style={{ color: '#9CA3AF', flexShrink: 0 }} />}
              </button>
            ) : (
              <input value={rempliPar} onChange={e => setRempliPar(e.target.value)}
                placeholder="Votre nom complet" data-testid="blv2-rempli-par-input"
                className="w-full px-4 py-3 rounded-xl border-2 text-base outline-none"
                style={{ borderColor: rempliPar.trim() ? '#22C55E' : '#E5E7EB', fontFamily: 'inherit' }} />
            )}
          </div>

          {/* Date effective */}
          <div className="rounded-2xl border p-4" style={{ borderColor: isRetour ? '#FDBA74' : '#E2E8F0', background: 'white' }}>
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={15} style={{ color: isRetour ? '#C2410C' : '#6B7280' }} />
              <p className="text-sm font-bold" style={{ color: '#374151' }}>
                Date réelle {isRetour ? 'du retour' : 'de la livraison'}
              </p>
            </div>
            <input type="datetime-local" value={dateEffective} onChange={e => { setDateEffective(e.target.value); setDateModified(true); }}
              data-testid="blv2-date-effective-input"
              className="w-full px-4 py-3 rounded-xl border-2 text-base outline-none"
              style={{ borderColor: dateEffective ? '#22C55E' : '#E5E7EB', fontFamily: 'inherit' }} />
          </div>

          {/* Critères — un bloc par ligne */}
          {(bl.lignes || []).map((l, i) => (
            <div key={i}>
              {(bl.lignes || []).length > 1 && (
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#9CA3AF' }}>
                  {l.categorie_nom || l.gamme_nom || `Machine ${i + 1}`}
                </p>
              )}
              {(l.criteres || []).length > 0 ? (
                <div className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: '#E2E8F0' }}>
                  <div className="p-3 space-y-0">
                    {l.criteres.map(c => {
                      const ligneId = l.ligne_bl_id || l.ligne_id;
                      return (
                        <CritereInput key={c.critere_id} critere={c}
                          value={critereValues[ligneId]?.[c.critere_id]}
                          onChange={val => setCritere(ligneId, c.critere_id, val)}
                          photoUrl={criterePhotos[ligneId]?.[c.critere_id]}
                          onPhotoUpload={file => handleCriterePhoto(ligneId, c.critere_id, file)}
                          uploadingPhoto={!!uploadingCriterePhoto[`${ligneId}_${c.critere_id}`]} />
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* ── Étape Photos ── */}
      {step === 'photos' && (
        <div className="flex-1 px-4 py-4 space-y-3 overflow-y-auto" style={{ paddingBottom: 100 }}>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#9CA3AF' }}>Photos générales</p>
          <p className="text-xs" style={{ color: '#6B7280' }}>Aspect général, côtés, casse éventuelle — max 1280×720, 300 Ko</p>
          <div className="grid grid-cols-2 gap-2">
            {generalPhotos.map((url, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <img src={url} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setGeneralPhotos(p => p.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 rounded-full p-1" style={{ background: 'rgba(0,0,0,0.5)', color: 'white' }}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <label className="flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed text-sm cursor-pointer"
            style={{ borderColor: '#E2E8F0', color: '#6B7280' }}>
            {uploadingPhoto ? <RefreshCw size={16} className="animate-spin" /> : <Camera size={16} />}
            <span>{uploadingPhoto ? 'Upload...' : 'Prendre / importer une photo'}</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleAddPhoto} disabled={uploadingPhoto} />
          </label>
        </div>
      )}

      {/* ── Étape Signature ── */}
      {step === 'signature' && (
        <div className="flex-1 px-4 py-4" style={{ paddingBottom: 24 }}>
          <SignatureCanvas onValidate={handleSubmit} />
        </div>
      )}

      {/* ── Modales (même présentation V1) ── */}
      {showSignatairePicker && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="flex-1" onClick={() => setShowSignatairePicker(false)} />
          <div className="rounded-t-3xl overflow-hidden" style={{ background: 'white', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E2E8F0' }}>
              <p className="text-base font-bold" style={{ color: '#111827' }}>Choisir le signataire</p>
              <button onClick={() => setShowSignatairePicker(false)} className="p-2 rounded-xl" style={{ background: '#F3F4F6' }}>
                <X size={18} style={{ color: '#6B7280' }} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {interlos.map((c, i) => {
                const isSelected = signataire === c.nom;
                return (
                  <button key={c.id || i} data-testid={`blv2-signataire-option-${i}`}
                    onClick={() => { setSignataire(c.nom); setShowSignatairePicker(false); }}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-all"
                    style={{ borderColor: isSelected ? '#22C55E' : '#E2E8F0', background: isSelected ? '#F0FDF4' : 'white' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                      style={{ background: '#EFF6FF', color: '#1e3a5f' }}>
                      {(c.nom || '').split(' ').map(w => w[0] || '').slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold truncate" style={{ color: '#111827' }}>{c.nom}</p>
                      {c.email && <p className="text-xs truncate" style={{ color: '#6B7280' }}>{c.email}</p>}
                    </div>
                    {isSelected && <Check size={20} style={{ color: '#22C55E', flexShrink: 0 }} />}
                  </button>
                );
              })}
              <button type="button" data-testid="blv2-signataire-manual-option"
                onClick={() => { setSignataireManual(true); setShowSignatairePicker(false); }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-dashed text-left active:scale-[0.98] transition-all"
                style={{ borderColor: '#CBD5E1', background: 'white' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#F3F4F6', color: '#6B7280' }}>
                  <Type size={16} />
                </div>
                <p className="text-base font-semibold" style={{ color: '#374151' }}>Autre signataire (saisie manuelle)</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAgentPicker && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="flex-1" onClick={() => setShowAgentPicker(false)} />
          <div className="rounded-t-3xl overflow-hidden" style={{ background: 'white', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E2E8F0' }}>
              <p className="text-base font-bold" style={{ color: '#111827' }}>Choisir l'agent de contrôle</p>
              <button onClick={() => setShowAgentPicker(false)} className="p-2 rounded-xl" style={{ background: '#F3F4F6' }}>
                <X size={18} style={{ color: '#6B7280' }} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {agents.map(a => {
                const fullName = [a.personne?.prenom || a.prenom, a.personne?.nom || a.nom].filter(Boolean).join(' ');
                const isSelected = rempliPar === fullName;
                const key = a.employe_id || a.personne_id;
                return (
                  <button key={key} data-testid={`blv2-agent-option-${key}`}
                    onClick={() => { setRempliPar(fullName); setShowAgentPicker(false); }}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-all"
                    style={{ borderColor: isSelected ? '#22C55E' : '#E2E8F0', background: isSelected ? '#F0FDF4' : 'white' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                      style={{ background: '#EFF6FF', color: '#1e3a5f' }}>
                      {((a.personne?.prenom || a.prenom || '')[0] || '').toUpperCase()}{((a.personne?.nom || a.nom || '')[0] || '').toUpperCase()}
                    </div>
                    <p className="flex-1 text-base font-semibold truncate" style={{ color: '#111827' }}>{fullName}</p>
                    {isSelected && <Check size={20} style={{ color: '#22C55E', flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer navigation ── */}
      <div className="fixed bottom-0 left-0 right-0 px-4 py-4 border-t" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        {step === 'criteres' && (
          <button onClick={() => {
            if (!signataire.trim()) { toast.error('Sélectionnez ou saisissez le signataire'); return; }
            if (!rempliPar.trim())  { toast.error('Saisissez le nom de l\'agent'); return; }
            setStep('photos');
          }}
            data-testid="blv2-btn-proceed-photos"
            className="w-full py-4 rounded-2xl text-base font-bold text-white"
            style={{ background: '#1e3a5f' }}>
            Contrôle validé — Photos
          </button>
        )}
        {step === 'photos' && (
          <div className="flex gap-2">
            <button onClick={() => setStep('criteres')} className="flex-1 py-3 rounded-2xl text-sm font-semibold border" style={{ borderColor: '#E2E8F0', color: '#6B7280' }}>Retour</button>
            <button onClick={() => setStep('signature')} data-testid="blv2-btn-proceed-signature"
              className="flex-1 py-4 rounded-2xl text-base font-bold text-white" style={{ background: '#1e3a5f' }}>
              Procéder à la signature
            </button>
          </div>
        )}
        {step === 'signature' && (
          <button onClick={() => setStep('photos')} className="w-full py-3 rounded-2xl text-sm font-semibold border" style={{ borderColor: '#E2E8F0', color: '#6B7280' }}>Retour aux photos</button>
        )}
      </div>
    </div>
  );
}


// ─── Page principale terrain BL ───────────────────────────────────────────────

export default function TerrainBonsLivraison() {
  const navigate = useNavigate();
  const [livraisons, setLivraisons] = useState([]);
  const [retours, setRetours] = useState([]);
  const [section, setSection] = useState('livraison'); // 'livraison' | 'retour'
  const [loading, setLoading] = useState(true);
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const [selectedBlId, setSelectedBlId] = useState(null);
  const [selectedBlSource, setSelectedBlSource] = useState(null); // 'v1' | 'v2'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Charger V1 et V2 en parallèle, puis fusionner les résultats
      const [r1, r2] = await Promise.allSettled([
        api.terrainListBonsLivraison(),
        api.terrainListBLV2(),
      ]);
      const liv1 = r1.status === 'fulfilled' ? (r1.value.data?.livraisons || []) : [];
      const ret1 = r1.status === 'fulfilled' ? (r1.value.data?.retours || []) : [];
      const liv2 = r2.status === 'fulfilled' ? (r2.value.data?.livraisons || []) : [];
      const ret2 = r2.status === 'fulfilled' ? (r2.value.data?.retours || []) : [];
      // Dédupliquer par bl_id : l'endpoint V1 inclut déjà les BL V2 (fusion backend)
      const mergeSansDoublons = (a, b) => {
        const seen = new Set();
        const out = [];
        for (const g of [...a, ...b]) {
          const bls = (g.bls || []).filter(bl => {
            if (!bl.bl_id || seen.has(bl.bl_id)) return false;
            seen.add(bl.bl_id);
            return true;
          });
          if (bls.length > 0) out.push({ ...g, bls });
        }
        return out;
      };
      setLivraisons(mergeSansDoublons(liv1, liv2));
      setRetours(mergeSansDoublons(ret1, ret2));
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vue : saisie ou lecture BL/BR
  if (selectedBlId) {
    if (selectedBlSource === 'v2') {
      // BL signé → lecture seule
      const blSigné = [...livraisons, ...retours]
        .flatMap(g => g.bls)
        .find(b => b.bl_id === selectedBlId);
      if (blSigné?.bl_statut === 'LIVRE') {
        return (
          <BLLectureV2
            blId={selectedBlId}
            onBack={() => setSelectedBlId(null)}
          />
        );
      }
      return (
        <BLSaisieV2
          blId={selectedBlId}
          onBack={() => setSelectedBlId(null)}
          onDone={() => { setSelectedBlId(null); setSelectedGroupe(null); load(); }}
        />
      );
    }
    return (
      <BLSaisie
        blId={selectedBlId}
        onBack={() => setSelectedBlId(null)}
        onDone={() => { setSelectedBlId(null); setSelectedGroupe(null); load(); }}
      />
    );
  }

  // Vue : liste BL d'un contrat
  if (selectedGroupe) {
    return (
      <BLListeContrat
        groupe={selectedGroupe}
        onSelectBL={(blId, source) => { setSelectedBlId(blId); setSelectedBlSource(source || 'v1'); }}
        onBack={() => setSelectedGroupe(null)}
      />
    );
  }

  const groupes = section === 'retour' ? retours : livraisons;
  const totalLiv = livraisons.reduce((s, g) => s + g.bls.length, 0);
  const totalRet = retours.reduce((s, g) => s + g.bls.length, 0);
  const isRetour = section === 'retour';

  // Groupement par date : aujourd'hui / demain / autres
  const todayStr    = todayInTz('Europe/Paris');
  const tomorrowStr = addDays(todayStr, 1);
  const getGroupeDate = (g) => {
    const bl = g.bls[0];
    const raw = isRetour ? bl?.date_fin : bl?.date_debut;
    return raw ? raw.slice(0, 10) : null;
  };
  const buckets = [
    { key: 'today',    label: "Aujourd'hui", color: '#1e3a5f', bg: '#EFF6FF', groupes: groupes.filter(g => getGroupeDate(g) === todayStr) },
    { key: 'tomorrow', label: 'Demain',       color: '#7C3AED', bg: '#F5F3FF', groupes: groupes.filter(g => getGroupeDate(g) === tomorrowStr) },
    { key: 'other',    label: 'Autre date',   color: '#6B7280', bg: '#F9FAFB', groupes: groupes.filter(g => { const d = getGroupeDate(g); return !d || (d !== todayStr && d !== tomorrowStr); }) },
  ].filter(b => b.groupes.length > 0);

  // Vue : liste des contrats avec BL/BR en attente
  return (
    <div className="flex flex-col" style={{ minHeight: '100vh', background: '#F8FAFF' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#E2E8F0', background: 'white' }}>
        <button onClick={() => navigate('/terrain')} className="p-2 rounded-xl border" style={{ borderColor: '#E2E8F0' }}>
          <ArrowLeft size={18} style={{ color: '#6B7280' }} />
        </button>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6B7280' }}>Mode terrain</p>
          <h2 className="text-xl font-bold" style={{ color: '#1e3a5f',  }}>
            {isRetour ? 'Bons de retour' : 'Bons de livraison'}
          </h2>
        </div>
      </div>

      {/* Onglets Livraison / Retour */}
      <div className="flex gap-2 px-4 pt-4">
        <button
          data-testid="section-livraison"
          onClick={() => setSection('livraison')}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl border-2 font-bold text-sm transition-all"
          style={{
            borderColor: !isRetour ? '#1e3a5f' : '#E2E8F0',
            background: !isRetour ? '#EFF6FF' : 'white',
            color: !isRetour ? '#1e3a5f' : '#9CA3AF',
          }}>
          <Package size={16} />
          Livraisons
          {totalLiv > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
              style={{ background: '#1e3a5f', color: 'white' }}>
              {totalLiv}
            </span>
          )}
        </button>
        <button
          data-testid="section-retour"
          onClick={() => setSection('retour')}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl border-2 font-bold text-sm transition-all"
          style={{
            borderColor: isRetour ? '#C2410C' : '#E2E8F0',
            background: isRetour ? '#FFF7ED' : 'white',
            color: isRetour ? '#C2410C' : '#9CA3AF',
          }}>
          <RotateCcw size={16} />
          Retours
          {totalRet > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
              style={{ background: '#C2410C', color: 'white' }}>
              {totalRet}
            </span>
          )}
        </button>
      </div>

      <div className="px-4 pt-3 space-y-3 pb-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: '#1e3a5f', borderTopColor: 'transparent' }} />
          </div>
        ) : groupes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3"
            style={{ color: '#9CA3AF' }}>
            <Clock size={40} style={{ opacity: 0.3 }} />
            <p className="text-base font-bold" style={{  }}>
              Aucun bon {isRetour ? 'de retour' : 'de livraison'} en attente
            </p>
            <p className="text-sm text-center px-8">
              {isRetour
                ? 'Les bons de retour apparaissent ici lorsqu\'un contrat est mis en retour'
                : 'Les bons de livraison apparaissent ici lorsqu\'un contrat est mis en livraison'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {buckets.map(bucket => (
              <div key={bucket.key}>
                {/* En-tête de groupe date */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg"
                    style={{ background: bucket.bg, color: bucket.color }}>
                    {bucket.label}
                  </span>
                  <span className="text-xs" style={{ color: '#9CA3AF' }}>
                    {bucket.groupes.length} contrat{bucket.groupes.length > 1 ? 's' : ''}
                  </span>
                </div>

                {/* Cartes contrats */}
                <div className="space-y-3">
                  {bucket.groupes.map(g => (
                    <button key={g.contrat_id}
                      data-testid={`contrat-groupe-${g.contrat_id}`}
                      onClick={() => {
                        if (g.bls.length === 1) {
                          setSelectedBlId(g.bls[0].bl_id);
                          setSelectedBlSource(g.bls[0].source || g.source || 'v1');
                        } else {
                          setSelectedGroupe(g);
                        }
                      }}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left active:scale-[0.98] transition-transform"
                      style={{ borderColor: '#E2E8F0', background: 'white' }}>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ background: isRetour ? '#FFF7ED' : '#EFF6FF' }}>
                        {isRetour
                          ? <RotateCcw size={26} style={{ color: '#C2410C' }} />
                          : <Package size={26} style={{ color: '#1e3a5f' }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold" style={{ color: '#111827',  }}>
                          {g.client_nom || '—'}
                        </p>
                        <p className="text-sm" style={{ color: '#6B7280' }}>
                          {g.contrat_numero}
                          {g.bls.length === 1 && (g.bls[0].categorie_nom || g.bls[0].materiel_nom) && (
                            <span style={{ color: '#9CA3AF' }}>
                              {g.bls[0].categorie_nom ? ` — ${g.bls[0].categorie_nom}` : ''}
                              {g.bls[0].materiel_nom ? ` · ${g.bls[0].materiel_nom}` : ''}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {g.total_count > 0 && g.signe_count > 0 ? (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{ background: '#F0FDF4', color: '#16A34A' }}>
                            {g.signe_count}/{g.total_count} signés
                          </span>
                        ) : g.bls.length > 1 ? (
                          <span className="text-sm font-bold px-2.5 py-1 rounded-full"
                            style={{
                              background: isRetour ? '#FFF7ED' : '#EFF6FF',
                              color: isRetour ? '#C2410C' : '#1e3a5f',
                            }}>
                            {g.bls.length} {isRetour ? 'BR' : 'BL'}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{
                              background: isRetour ? '#FFF7ED' : '#FFFBEB',
                              color: isRetour ? '#C2410C' : '#D97706',
                            }}>En attente</span>
                        )}
                        <ChevronRight size={18} style={{ color: '#9CA3AF' }} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
